import React, { useEffect, useState, memo, useRef } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../../../../shared/hooks/useApi';
import CustomTable from '../../../../../../shared/components/table/custom-table';
import { LoaderComponent } from '../../../../../../shared/components/loaders/page-loader';
import { fetchProductaddDetails } from './product-api';
import { useTransactionCertificateStore } from '../../../../../../store/transaction-certificates.store';

interface RenderDetailPanelProps {
  row: any;
}

// Column Definitions
const firstProcessorColumns = [
  { accessorKey: 'tcProductFirstProcessorTeId', header: 'First Processor TE-ID', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductFirstProcessCode', header: 'First Process Code', minSize: 60, maxSize: 150 },
];

const facilitiesColumns = [
  { accessorKey: 'tcProductFacilitiesTeId', header: 'Facilities TE-ID', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductFacilitiesProcessCode', header: 'Process Code', minSize: 60, maxSize: 150 },
];

const geographicColumns = [
  { accessorKey: 'tcProductCertifiedRawMaterialCode', header: 'Raw Material Code', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialCodeDescription', header: 'Raw Material Description', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialCountryOrArea', header: 'Country/Area', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialCountryOrAreaDescription', header: 'Country/Area Description', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialStateOrProvince', header: 'State/Province', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialStateOrProvinceDescription', header: 'State/Province Description', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialWeight', header: 'Weight', minSize: 60, maxSize: 150 },
];

const materialColumns = [
  { accessorKey: 'tcProductInfoFiberLength', header: 'Fiber Length (mm)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoYarnCount', header: 'Yarn Count', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoFabricWeightAndConstruct', header: 'Fabric Weight and Construct (kg)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoFabricWeightOfFinishedProduct', header: 'Fabric Weight of Finished Product (kg)', minSize: 60, maxSize: 150 },
];

const standardColumns = [
  { accessorKey: 'tcProductClaimsCategory', header: 'Claims Category', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialCodeClaimsCategory', header: 'Raw Material', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedWeight', header: 'Certified Weight (kg)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialCodeClaimsCategoryDescription', header: 'Raw Material Description', minSize: 60, maxSize: 150 }
];

const rawMaterialColumns = [
  { accessorKey: 'tcProductRawMaterialCode', header: 'Raw Material Code', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialPercentage', header: 'Raw Material Percentage', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialCodeDescription', header: 'Raw Material Description', minSize: 60, maxSize: 150 },
];

const RenderDetailPanel = memo(({ row }: RenderDetailPanelProps) => {
  const rowData = row.original;
  const { TE_ID } = useParams();

  const { loading, executeGet } = useApi();
  const [productData, setProductData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const productId = rowData?.productId;
  const shipmentId = rowData?.shipmentId;
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!productId || !TE_ID || !shipmentId || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    fetchProductaddDetails(executeGet, TE_ID, productId, shipmentId)
      .then((data) => {
        const tcProductInformation = data?.data?.tcProductInformation || [data?.data] || [data] || [];
        setProductData(tcProductInformation);
      })
      .catch((err) => {setError(err.message)});
  }, [productId, shipmentId, TE_ID, executeGet]);

  if (loading) {
    return <LoaderComponent />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!productData || productData.length === 0) {
    return <Typography>No product details available.</Typography>;
  }

  // Data Extraction: Since productData is an array
  const store = useTransactionCertificateStore.getState();
  const product = productData[0]; // Assuming one item per detail panel (adjust if multiple)

  const firstProcessorInfo = product.tcProductFirstProcessorInformation || [];
  const facilitiesInfo = product.tcProductFacilitiesInformation || [];
  const geographicInfo = (product.tcProductCertifiedRawMaterialGeographicInformation || []).map((info: any) => {
    const countryList = Array.isArray(store?.getCountryCodes) ? store.getCountryCodes : [];
    const countryDesc = countryList.find((option: any) => option.value === info.tcProductCertifiedRawMaterialCountryOrArea)?.label || info.tcProductCertifiedRawMaterialCountryOrAreaDescription || "";
    return {
      ...info,
      tcProductCertifiedRawMaterialCountryOrAreaDescription: countryDesc,
      tcProductCertifiedRawMaterialStateOrProvinceDescription: info.tcProductCertifiedRawMaterialStateOrProvinceDescription || "",
    };
  });

  const materialInfo = {
    tcProductInfoFiberLength: product.tcProductInfoFiberLength,
    tcProductInfoYarnCount: product.tcProductInfoYarnCount,
    tcProductInfoFabricWeightAndConstruct: product.tcProductInfoFabricWeightAndConstruct,
    tcProductInfoFabricWeightOfFinishedProduct: product.tcProductInfoFabricWeightOfFinishedProduct
  };

  const standardInfo = product.tcProductCertifiedWeightInformation || [];
  const rawMaterialInfo = product.tcProductRawMaterialInformation || [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 5,
        gap: 4,
        width: '100%',
      }}
    >
      {/* First Processor Info */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          First Processor Information
        </Typography>
        <CustomTable
          columns={firstProcessorColumns}
          data={firstProcessorInfo}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Fabric & Material Details */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Fabric and Material Details
        </Typography>
        <CustomTable
          columns={materialColumns}
          data={[materialInfo]}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Standard Details */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Standard Details
        </Typography>
        <CustomTable
          columns={standardColumns}
          data={standardInfo}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Raw Material Composition */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Raw Material Composition
        </Typography>
        <CustomTable
          columns={rawMaterialColumns}
          data={rawMaterialInfo}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Facilities Information */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Facilities Information
        </Typography>
        <CustomTable
          columns={facilitiesColumns}
          data={facilitiesInfo}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Certified Raw Material Geographic Information */}
      <Box sx={{ width: '100%', textAlign: 'left' }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Certified Raw Material Geographic Information
        </Typography>
        <CustomTable
          columns={geographicColumns}
          data={geographicInfo}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
    </Box>
  );
});

export default RenderDetailPanel;